import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import HeadLogo from '../Assets/Oolook_logo.png';
import "react-toastify/dist/ReactToastify.css";

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({ strength: 'Weak', color: 'bg-danger', width: 'w-1/3' });
    const [isLoading, setIsLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const evaluatePasswordStrength = (password) => {
        const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        const mediumRegex = /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;

        if (strongRegex.test(password)) {
            setPasswordStrength({ strength: 'Strong', color: 'bg-success', width: 'w-full' });
        } else if (mediumRegex.test(password)) {
            setPasswordStrength({ strength: 'Medium', color: 'bg-yellow-500', width: 'w-2/3' });
        } else {
            setPasswordStrength({ strength: 'Weak', color: 'bg-danger', width: 'w-1/3' });
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (passwordStrength.strength === 'Weak') {
            toast.error('Password is too weak. Please include letters, numbers, and special characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API}/user/reset-password`, {
                token,
                newPassword,
            });
            toast.success('Password reset successfully! Redirecting to login...');
            setTimeout(() => navigate('/'), 2000);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Invalid or expired token. Please request a new link.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-neutral-light">
            <ToastContainer position="top-right" autoClose={5000} />
            <div className="auth-card">
                <img src={HeadLogo} alt="Oolook Logo" className="mx-auto h-12 w-auto mb-6" />
                <h2 className="text-center text-2xl font-bold text-neutral-dark mb-2">Create New Password</h2>
                <p className="text-center text-sm text-neutral-muted mb-8">
                    Your new password must be secure and different from previous ones.
                </p>

                <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-neutral-muted mb-1">New Password</label>
                        <div className="relative">
                            <input
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    evaluatePasswordStrength(e.target.value);
                                }}
                                className="form-input pr-10"
                            />
                             <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-muted"
                                aria-label={showNewPassword ? "Hide password" : "Show password"}
                            >
                                <i className={`fa-regular ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                        {newPassword && (
                            <div className="mt-2">
                                <div className={`h-1.5 w-full bg-gray-200 rounded-full`}>
                                    <div className={`h-full rounded-full ${passwordStrength.color} ${passwordStrength.width} transition-all duration-300`}></div>
                                </div>
                                <p className={`text-xs mt-1 font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                                    Strength: {passwordStrength.strength}
                                </p>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-neutral-muted mb-1">Confirm Password</label>
                         <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="form-input pr-10"
                            />
                             <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-muted"
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                <i className={`fa-regular ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                    </div>
                    <div className="pt-4">
                        <button type="submit" className="btn-primary" disabled={isLoading}>
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;